import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useAuthProvider } from '@askporter/auth-provider';
import { DynamicError } from '@askporter/component-library';
import { useConfig, useTranslation } from '@askporter/config-provider';
import { ProfileProvider } from '@askporter/profile-provider';
import { NewPasswordProps, PublicPageProps, ResetPasswordProps } from '@askporter/public-pages';
import { GeneralErrorBoundaryWithRumIfEnabled } from '@askporter/rum';
import { OnsiteWebchatPageProps, WebchatPageProps } from '@askporter/webchat-pages';
import useSetLanguage from '../../hooks/useSetLanguage';
import Rum from '../../providers/Rum';
import { lazyWithRetry, useForgotPassword, config } from '../../utils';
import { API } from '../../utils/api';
import CoreHeader from '../CoreHeader';
import FooterWithLangPicker from '../FooterWithLangPicker';
import SemiPrivateRoute from '../SemiPrivateRoute';

const Landing = lazyWithRetry<PublicPageProps>(() => import('@askporter/public-pages/src/pages/Landing'));
const SignUpPage = lazyWithRetry<PublicPageProps>(() => import('@askporter/public-pages/src/pages/SignUp'));
const SignInPage = lazyWithRetry<PublicPageProps>(() => import('@askporter/public-pages/src/pages/SignIn'));
const ResetPassword = lazyWithRetry<ResetPasswordProps>(
  () => import('@askporter/public-pages/src/pages/ResetPassword'),
);
const NewPassword = lazyWithRetry<NewPasswordProps>(() => import('@askporter/public-pages/src/pages/NewPassword'));
const Confirmation = lazyWithRetry(() => import('@askporter/public-pages/src/pages/EmailVerification/Confirmation'));
const Activate = lazyWithRetry<PublicPageProps>(
  () => import('@askporter/public-pages/src/pages/EmailVerification/Activate'),
);
const Activated = lazyWithRetry<PublicPageProps>(
  () => import('@askporter/public-pages/src/pages/EmailVerification/Activated'),
);
const Privacy = lazyWithRetry<PublicPageProps>(() => import('@askporter/public-pages/src/pages/Privacy'));
const Terms = lazyWithRetry<PublicPageProps>(() => import('@askporter/public-pages/src/pages/Terms'));
const Guest = lazyWithRetry<WebchatPageProps>(() => import('@askporter/webchat-pages/src/Guest'));
const Outbound = lazyWithRetry<WebchatPageProps>(() => import('@askporter/webchat-pages/src/Outbound'));
const TChat = lazyWithRetry<WebchatPageProps>(() => import('@askporter/webchat-pages/src/TChat'));
const OnSite = lazyWithRetry<OnsiteWebchatPageProps>(() => import('@askporter/webchat-pages/src/OnSite'));

const PublicRouter: React.FC = () => {
  const { featureFlags } = useConfig();
  const { authToken } = useAuthProvider();
  const { isLanguageConsistent, setLanguage } = useSetLanguage();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { disableRegistration, enableRegistrationWithToken } = featureFlags || {};
  const profileProviderProps = {
    authToken,
    isLanguageConsistent,
    setLanguage,
    API,
  };

  const webchatConfig = {
    DEV_ASSISTANT_SECRET: config.DEV_ASSISTANT_SECRET,
  };
  const { handleStart, startLoading, confirmNewPassword, confirmNewPasswordLoading } = useForgotPassword();

  return (
    <Switch>
      <Route
        path="/"
        exact={true}
        render={() => {
          if (disableRegistration || enableRegistrationWithToken)
            return <SignInPage footer={<FooterWithLangPicker />} />;
          return <Landing footer={<FooterWithLangPicker />} />;
        }}
      />
      <Route path="/sign-in" exact={true} render={() => <SignInPage footer={<FooterWithLangPicker />} />} />
      <Route path="/sign-up" exact={true} render={() => <SignUpPage footer={<FooterWithLangPicker />} />} />
      <Route
        path="/404"
        exact={true}
        render={() => (
          <ProfileProvider {...profileProviderProps}>
            <DynamicError header={<CoreHeader />} t={t} language={language} errTitle="404" />
          </ProfileProvider>
        )}
      />
      <Route
        path="/500"
        exact={true}
        render={() => (
          <ProfileProvider {...profileProviderProps}>
            <DynamicError
              header={<CoreHeader />}
              t={t}
              language={language}
              errTitle="500"
              errMsg={`${t('ns.error_page:500:subtitle')}`}
              errMetaTitle={`${t('ns.error_page:500:page_title')}`}
              errLinkText={`${t('ns.error_page:500:link_text')}`}
            />
          </ProfileProvider>
        )}
      />
      <Route path="/activate" exact={true} render={() => <Activate footer={<FooterWithLangPicker />} />} />
      <Route path="/activated" exact={true} render={() => <Activated footer={<FooterWithLangPicker />} />} />
      <Route path="/confirmation" render={() => <Confirmation />} />
      <Route
        path="/reset-password"
        render={() => (
          <ResetPassword footer={<FooterWithLangPicker />} handleStart={handleStart} startLoading={startLoading} />
        )}
      />
      <Route
        path="/new-password"
        render={() => (
          <NewPassword
            confirmNewPassword={confirmNewPassword}
            confirmNewPasswordLoading={confirmNewPasswordLoading}
            footer={<FooterWithLangPicker />}
          />
        )}
      />
      <Route path="/privacy" render={() => <Privacy footer={<FooterWithLangPicker />} />} />
      <Route path="/terms" render={() => <Terms footer={<FooterWithLangPicker />} />} />

      <ProfileProvider {...profileProviderProps}>
        <Route
          path="/outbound/:inviteCode"
          render={() => <Outbound CoreHeader={CoreHeader} webchatConfig={webchatConfig} />}
        />
        <Route path="/guest/:context?" render={() => <Guest CoreHeader={CoreHeader} webchatConfig={webchatConfig} />} />
        <Rum>
          <GeneralErrorBoundaryWithRumIfEnabled header={<CoreHeader />}>
            <Route path="/tchat" render={() => <TChat CoreHeader={CoreHeader} webchatConfig={webchatConfig} />} />
            <SemiPrivateRoute
              path="/onsite/:assetUID"
              privateProps={{ shouldGetUserToken: true, CoreHeader, webchatConfig }}
              publicProps={{ CoreHeader, webchatConfig }}
              Component={OnSite}
            />
            {/* handle cases where the asset is missing */}
            <Route path="/onsite" exact={true}>
              <Redirect from="*" to="/404" />
            </Route>
          </GeneralErrorBoundaryWithRumIfEnabled>
        </Rum>
      </ProfileProvider>

      <Redirect from="*" to="/404" />
    </Switch>
  );
};

export default PublicRouter;
